import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AMAZON_ERROR,
  PAYMENT_BLOCK,
} from '../../blocks/Checkout/SteppedCheckout/constants';
import { IS_NORMAL_AMAZON_CHECKOUT_KEY } from '../../components/CompleteOrderButton';
import amazonPayService, {
  AMAZON_PAY_STATE_CANCELED,
} from '../../services/amazonPay';
import { placeOrder } from '../../slices/orders';
import {
  setNeedsToCheckAmazonPayShippingAddressDraft,
  setAmazonPayShippingAddressDraft,
} from '../../slices/shippingAddress';
import {
  paymentMethods,
  setAmazonCheckoutSessionId,
} from '../../slices/payments';
import {
  focusAndScrollTo,
  formatPhone,
  getLastURIComponent,
} from '../../utils';
import storage from '../../utils/storage';

const amazonBuyerToAddress = ({ shippingAddress, buyer }) => ({
  streetAddress: shippingAddress.addressLine1,
  city: shippingAddress.city,
  state: shippingAddress.stateOrRegion,
  postalCode: shippingAddress.postalCode,
  country: shippingAddress.countryCode,
  // TODO: review, because amazon has the name and the lastname in the same property
  firstName: shippingAddress.name.split(' ').shift(),
  lastName: shippingAddress.name.split(' ').pop(),
  email: buyer.email,
  additionalStreetInfo: shippingAddress.addressLine2,
  phone: shippingAddress.phoneNumber
    ? formatPhone(shippingAddress.phoneNumber)
    : null,
});

const useAmazonPay = ({
  amazonPayCheckoutCreated,
  amazonPayApprovedFromUrl,
  amazonCheckoutSessionIdFromUrl,
}) => {
  const dispatch = useDispatch();
  const amazonError = useRef(null);
  const [amazonPayApproved, setAmazonPayApproved] = useState(false);
  const [amazonPayAddressLoading, setAmazonPayAddressLoading] = useState(false);
  const amazonCheckoutSessionId = useSelector(
    (state) => state.payments.amazonCheckoutSessionId,
  );
  const { kountSessionId } = useSelector((state) => state.payments);
  const cartLoaded = useSelector((state) => !!state.cart.id);

  const [shouldPlaceOrder, setShouldPlaceOrder] = useState(false);

  useEffect(() => {
    if (amazonCheckoutSessionId) {
      storage.cleanAllPaymentData();
      localStorage.setItem('amazonCheckoutSessionId', amazonCheckoutSessionId);
    }
  }, [amazonCheckoutSessionId]);

  useEffect(() => {
    if (amazonPayApprovedFromUrl) {
      setAmazonPayApproved(amazonPayApprovedFromUrl);
    }

    if (amazonCheckoutSessionIdFromUrl) {
      dispatch(setAmazonCheckoutSessionId(amazonCheckoutSessionIdFromUrl));
    }
  }, [dispatch, amazonPayApprovedFromUrl, amazonCheckoutSessionIdFromUrl]);

  useEffect(() => {
    if (
      amazonPayCheckoutCreated &&
      !amazonPayApproved &&
      !localStorage.getItem(IS_NORMAL_AMAZON_CHECKOUT_KEY)
    ) {
      setAmazonPayAddressLoading(true);
    }
  }, [amazonPayCheckoutCreated, amazonPayApproved]);

  useEffect(() => {
    if (shouldPlaceOrder) {
      dispatch({
        type: 'setStatus',
        status: 'loading',
      });

      dispatch(
        placeOrder({
          orderData: {
            paymentMethod: paymentMethods.AMAZON_PAY,
            id: amazonCheckoutSessionId,
            sessionId: kountSessionId,
          },
        }),
      );
    }
  }, [amazonCheckoutSessionId, dispatch, kountSessionId, shouldPlaceOrder]);

  // Amazon Pay
  useEffect(() => {
    const createOrderIfPaymentSucceeded = async () => {
      let amazonCheckoutSession;

      if (amazonCheckoutSessionId) {
        amazonCheckoutSession = await amazonPayService.getCheckoutSession({
          checkoutSessionId: amazonCheckoutSessionId,
        });
      }
      if (
        amazonCheckoutSession?.statusDetails?.state ===
        AMAZON_PAY_STATE_CANCELED
      ) {
        amazonError.current = AMAZON_PAY_STATE_CANCELED;
        dispatch({
          type: 'addError',
          errorBlock: PAYMENT_BLOCK,
          errorCode: AMAZON_ERROR,
        });
        dispatch(setAmazonCheckoutSessionId(null));
        localStorage.removeItem('amazonCheckoutSessionId');
      } else {
        amazonError.current = null;
      }
      if (amazonPayApproved) {
        setShouldPlaceOrder(true);
      } else if (amazonPayCheckoutCreated && amazonCheckoutSession) {
        if (
          amazonCheckoutSession.shippingAddress &&
          amazonCheckoutSession.buyer &&
          !localStorage.getItem(IS_NORMAL_AMAZON_CHECKOUT_KEY)
        ) {
          const shippingAddressFromAmazon = amazonBuyerToAddress(
            amazonCheckoutSession,
          );

          const lastURIComponent = getLastURIComponent();
          if (lastURIComponent.toLocaleLowerCase() === 'checkout') {
            focusAndScrollTo('shipping-email');
          }

          if (lastURIComponent.toLocaleLowerCase() === 'checkout') {
            dispatch(
              setAmazonPayShippingAddressDraft({
                ...shippingAddressFromAmazon,
                streetName: shippingAddressFromAmazon?.streetAddress,
                additionalStreetInfo: '',
              }),
            );
            dispatch(setNeedsToCheckAmazonPayShippingAddressDraft(true));
          }
        }
        setAmazonPayAddressLoading(false);
      }
    };
    if (cartLoaded) {
      createOrderIfPaymentSucceeded();
    }
  }, [
    amazonCheckoutSessionId,
    amazonPayApproved,
    amazonPayCheckoutCreated,
    cartLoaded,
    dispatch,
    kountSessionId,
  ]);

  return (
    (amazonCheckoutSessionId && amazonPayApproved && !amazonError.current) ||
    amazonPayAddressLoading
  );
};

export default useAmazonPay;
